import { ceil, floor, round } from 'lodash-es';

export const nearestNumber = (
  value: number,
  nearest: number,
  operation: 'floor' | 'round' | 'ceil'
) => {
  const mapping = {
    floor,
    ceil,
    round,
  };

  const fn = mapping[operation];

  return fn(value / nearest) * nearest;
};

/**
 * Rounds a given value down to the nearest multiple of the specified number
 * @param value The value to be rounded down.
 * @param nearest The multiple to which the value will be rounded down.
 * @returns The rounded value, which is the closest multiple of `nearest` that is less than or equal to the `value`.
 *
 * @example
 * floorNearest(123, 10); // returns 120
 * floorNearest(15, 5) // returns 15
 * floorNearest(97, 25) // returns 75
 */
export const floorNearest = (value: number, nearest: number) =>
  nearestNumber(value, nearest, 'floor');

export const ceilNearest = (value: number, nearest: number) =>
  nearestNumber(value, nearest, 'ceil');

/**
 * Rounds a given time interval down to the nearest minute, considering the provided interval in minutes.
 * @param interval The time interval in milliseconds to be rounded.
 * @param intervalInMinutes The interval in minutes to which the `interval` should be rounded.
 * @returns The rounded time interval in milliseconds, which is a multiple of `1000 * 60 * intervalInMinutes`.
 *
 * @example
 * nearestMinute(123456789, 5); // returns 123300000
 * nearestMinute(987654321, 10); // returns 987600000
 * nearestMinute(450000, 1); // returns 420000
 */
export const nearestMinute = (interval: number, intervalInMinutes: number) =>
  floorNearest(interval, 1000 * 60 * intervalInMinutes);

export const farthestMinute = (interval: number, intervalInMinutes: number) =>
  ceilNearest(interval, 1000 * 60 * intervalInMinutes);

export const divmod = (dividend: number, divisor: number): [number, number] => {
  const quotient = Math.floor(dividend / divisor);
  const remainder = dividend % divisor;

  return [quotient, remainder];
};
